import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Users, Package, CalendarDays, ArrowUpRight, ArrowDownRight, Megaphone } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { rupiah, tanggal, bulanLabel, PRAYER_LABELS } from "@/lib/format";
import { StatCard } from "@/components/admin/StatCard";
import { PageHeader } from "@/components/admin/PageHeader";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [prayer, setPrayer] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/prayer/today").then((r) => setPrayer(r.data)).catch(() => {});
  }, []);

  const chart = (stats?.monthly || []).map((m) => ({ ...m, label: bulanLabel(m.bulan) }));

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard" subtitle="Ringkasan kondisi masjid hari ini" />
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Saldo Kas" value={rupiah(stats?.saldo)} icon={Wallet} tone="bg-[#f6ebd0] text-[#a8871f]" testId="stat-saldo" />
        <StatCard label="Jumlah Jamaah" value={stats?.jumlah_jamaah ?? "-"} icon={Users} testId="stat-jamaah" />
        <StatCard label="Item Inventaris" value={stats?.jumlah_inventaris ?? "-"} icon={Package} testId="stat-inventaris" />
        <StatCard label="Kegiatan Bulan Ini" value={stats?.kegiatan_bulan_ini ?? "-"} icon={CalendarDays} testId="stat-kegiatan" />
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        <StatCard label="Kas Masuk Bulan Ini" value={rupiah(stats?.bulan_ini_masuk)} icon={ArrowUpRight} tone="bg-emerald-50 text-emerald-700" testId="stat-masuk-bulan" />
        <StatCard label="Kas Keluar Bulan Ini" value={rupiah(stats?.bulan_ini_keluar)} icon={ArrowDownRight} tone="bg-red-50 text-red-700" testId="stat-keluar-bulan" />
        <StatCard label="Pengumuman Aktif" value={stats?.pengumuman_aktif ?? "-"} icon={Megaphone} testId="stat-pengumuman" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-emerald-900/10 p-6" data-testid="dashboard-chart">
          <h3 className="font-semibold text-emerald-950">Arus Kas 6 Bulan Terakhir</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} barGap={4}>
                <CartesianGrid vertical={false} stroke="#e8ede8" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={44} />
                <Tooltip formatter={(v) => rupiah(v)} cursor={{ fill: "#f3f6f1" }} />
                <Bar dataKey="masuk" name="Masuk" fill="#145c48" radius={[6, 6, 0, 0]} />
                <Bar dataKey="keluar" name="Keluar" fill="#d4af37" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-3xl emerald-bg pattern-overlay text-white p-6" data-testid="dashboard-prayer-widget">
            <p className="text-xs uppercase tracking-widest text-[#80b996] font-semibold">Jadwal Sholat Hari Ini</p>
            <p className="text-sm text-emerald-200 mt-1">{prayer?.hari}, {prayer?.tanggal}</p>
            <ul className="mt-4 space-y-2">
              {["subuh", "dzuhur", "ashar", "maghrib", "isya"].map((k) => (
                <li key={k} className="flex justify-between text-sm border-b border-white/10 pb-2 last:border-0">
                  <span className="text-emerald-100">{PRAYER_LABELS[k]}</span>
                  <span className="font-bold gold-text">{prayer?.times?.[k] || "--:--"}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-white border border-emerald-900/10 p-6" data-testid="dashboard-upcoming">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-emerald-950">Agenda Mendatang</h3>
              <Link to="/admin/kegiatan" className="text-xs text-emerald-700 underline" data-testid="dashboard-link-kegiatan">Kelola</Link>
            </div>
            <ul className="mt-3 space-y-3">
              {(stats?.kegiatan_mendatang || []).map((a) => (
                <li key={a.id} className="text-sm">
                  <p className="font-medium text-emerald-950">{a.title}</p>
                  <p className="text-xs text-slate-500">{tanggal(a.date)} {a.time && `· ${a.time} WIB`}</p>
                </li>
              ))}
              {stats && stats.kegiatan_mendatang?.length === 0 && <li className="text-sm text-slate-400">Tidak ada agenda.</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
